/**
 * [MCV-1948] Helpers for noticing that the tab is running an older build than the one now
 * deployed. Vite emits the entry as `/assets/index-<hash>.js`, and nginx serves `/`
 * (index.html) with `Cache-Control: no-store`, so the entry path named by a fresh index.html
 * changes with every build that changes the app.
 */
const ENTRY_PATHNAME = /^\/assets\/index-[^/]+\.js$/;

/** The pathname of `src` when it is the Vite entry chunk, otherwise null. */
const entryPathname = (src: string | null | undefined): string | null => {
  if (!src) return null;
  try {
    const { pathname } = new URL(src, window.location.href);
    return ENTRY_PATHNAME.test(pathname) ? pathname : null;
  } catch {
    return null;
  }
};

/** The entry script this tab was loaded with; null under the dev server (`/src/main.tsx`). */
export const currentEntryScriptPath = (doc: Document = document): string | null => {
  for (const script of Array.from(doc.querySelectorAll<HTMLScriptElement>('script[src]'))) {
    if (script.type !== 'module') continue;
    const pathname = entryPathname(script.getAttribute('src'));
    if (pathname) return pathname;
  }
  return null;
};

const SCRIPT_TAG = /<script\b[^>]*>/gi;
const MODULE_TYPE = /\btype\s*=\s*["']module["']/i;
const SRC_ATTRIBUTE = /\bsrc\s*=\s*(?:"([^"]*)"|'([^']*)')/i;

/** The entry script path named by a fetched index.html, or null when it names none. */
export const parseEntryScriptPath = (html: string): string | null => {
  for (const tag of html.match(SCRIPT_TAG) ?? []) {
    if (!MODULE_TYPE.test(tag)) continue;
    const match = SRC_ATTRIBUTE.exec(tag);
    const pathname = entryPathname(match?.[1] ?? match?.[2]);
    if (pathname) return pathname;
  }
  return null;
};

/** The entry script path of the currently deployed index.html; null on any failure. */
export const fetchLatestEntryScriptPath = async (signal?: AbortSignal): Promise<string | null> => {
  try {
    const response = await fetch('/', {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      signal,
    });
    if (!response.ok) return null;
    return parseEntryScriptPath(await response.text());
  } catch {
    return null;
  }
};
